// apps/web/src/pages/vendas/detalhe/lotes-do-item.tsx — Fase 12, Plano 21 (PV-05, PV-06).
// Seção "Lotes" da linha expandida do item: Lote · OP · NF · Recebido · Etapa atual · "Ver no Kanban".
// Só exibe o que a API devolve (etapa atual já vem derivada do Kanban); nenhuma regra é recalculada aqui.
import { useState, type ComponentType } from 'react';
import { AlertTriangle, Ban, CheckCircle2, CircleDot, Clock, PauseCircle, Play, RotateCcw } from 'lucide-react';
import type { EstadoCartaoKanban, LoteDoItemPv } from '@pieltec/contracts';
import { rotuloEstado } from '../../../lib/kanban/apresentacao';
import { rotuloContagemLotes } from '../../../lib/vendas/apresentacao';
import { formatarQuantidade } from '../../../lib/vendas/formatacao';
import { Button } from '../../../components/ui/button';
import { GerarOpDialog } from './gerar-op-dialog';
import { apresentarAlertaCancelamento } from '../../../lib/pcp/alertas-cancelamento';
import { rotaCartaoControle } from '../../planejamento/kanban/encerrar-op-parcial';

type IconeLucide = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

const ICONES_ESTADO: Record<string, IconeLucide> = { CircleDot, Clock, PauseCircle, Ban, RotateCcw, CheckCircle2 };

function BadgeEtapa({ nome, estado }: { nome: string; estado: EstadoCartaoKanban }) {
  const rotulo = rotuloEstado(estado);
  const Icone = ICONES_ESTADO[rotulo.icone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${rotulo.classes}`}>
      {Icone && <Icone className="size-3 shrink-0" aria-hidden="true" />}
      {rotulo.texto}
      <span className="font-normal">· {nome}</span>
    </span>
  );
}

interface LotesDoItemProps {
  readonly lotes: readonly LoteDoItemPv[];
  readonly unidade: string;
  readonly numeroItem: number;
  /** Roteiro já definido no item do PV (pré-preenche o diálogo "Gerar OP"). */
  readonly roteiroId?: string | null;
  /** Item cancelado ou com saldo encerrado não gera OP. */
  readonly encerrado?: boolean;
  /** Usuário com pv:ajustar (a autorização real é do servidor). */
  readonly podeGerarOp?: boolean;
  readonly podeConsultarProducao?: boolean;
  /** Recarrega o PV depois que a OP foi gerada. */
  readonly onOpGerada?: () => void;
}

export function LotesDoItem({ lotes, unidade, numeroItem, roteiroId = null, encerrado = false, podeGerarOp = false, podeConsultarProducao = false, onOpGerada }: LotesDoItemProps) {
  const [loteParaGerarOp, setLoteParaGerarOp] = useState<LoteDoItemPv | null>(null);
  return (
    <section aria-label={`Lotes do item ${numeroItem}`} className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Lotes{lotes.length > 0 ? ` · ${rotuloContagemLotes(lotes.length)}` : ''}
      </h3>
      {lotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{rotuloContagemLotes(0)}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left">
            <tr>
              <th scope="col" className="py-1 pr-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lote</th>
              <th scope="col" className="py-1 pr-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">OP</th>
              <th scope="col" className="py-1 pr-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">NF</th>
              <th scope="col" className="py-1 pr-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recebido</th>
              <th scope="col" className="py-1 pr-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Etapa atual</th>
              <th scope="col" className="py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lotes.map((lote) => {
              const rotaCp = rotaCartaoControle(lote.loteId, lote.opId, podeConsultarProducao);
              return <tr key={lote.loteId} className="border-t border-border/60">
                <td className="py-1.5 pr-3 font-medium tabular-nums">{lote.loteInternoPieltec}</td>
                <td className="py-1.5 pr-3 tabular-nums">{lote.opNumero ?? '—'}</td>
                <td className="py-1.5 pr-3 tabular-nums">{lote.nfNumero ?? '—'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {formatarQuantidade(lote.recebido, unidade)} {unidade}
                </td>
                <td className="py-1.5 pr-3">
                  {lote.etapaAtual ? (
                    <BadgeEtapa nome={lote.etapaAtual.nome} estado={lote.etapaAtual.estado} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-1.5">
                  {apresentarAlertaCancelamento(lote.alertaCancelamentoFiscal === true) && (
                    <a href="/pcp/recebimentos?aba=pendencias" className="mb-1 flex min-h-11 items-center gap-1 text-xs font-medium text-amber-900 underline-offset-4 hover:underline">
                      <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
                      Cancelamento fiscal pendente
                    </a>
                  )}
                  {lote.opId === null && podeGerarOp && !encerrado && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setLoteParaGerarOp(lote)}
                      aria-label={`Gerar OP do lote ${lote.loteInternoPieltec}`}
                    >
                      <Play aria-hidden="true" />
                      Gerar OP
                    </Button>
                  )}
                  {lote.opNumero && (
                    <a
                      href={`/planejamento/kanban?op=${encodeURIComponent(lote.opNumero)}`}
                      className="inline-flex min-h-9 items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                      aria-label={`Ver no Kanban a OP ${lote.opNumero}`}
                    >
                      Ver no Kanban
                    </a>
                  )}
                  {rotaCp && (
                    <a href={rotaCp} className="ml-3 inline-flex min-h-9 items-center text-sm font-medium text-primary underline-offset-4 hover:underline">Cartão de Controle</a>
                  )}
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      )}
      {loteParaGerarOp && (
        <GerarOpDialog
          aberto
          onAbertoChange={(aberto) => {
            if (!aberto) setLoteParaGerarOp(null);
          }}
          loteId={loteParaGerarOp.loteId}
          loteInternoPieltec={loteParaGerarOp.loteInternoPieltec}
          roteiroIdInicial={roteiroId}
          onGerada={() => {
            setLoteParaGerarOp(null);
            onOpGerada?.();
          }}
        />
      )}
    </section>
  );
}
